import { useState } from "react";
import {
  Box,
  FormControlLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import moment, { Moment } from "moment";
import { DatePicker, Modal, SelectBox, TextField } from "../../../common";
import {
  RECURRENCE_FREQUENCY_DROPDOWN,
  RECURRENCE_MAX_INTERVAL,
  RECURRENCE_MAX_OCCURRENCES,
  RECURRENCE_WEEKDAY_INITIALS,
} from "../../../../constants";
import {
  MonthlyRepeatBy,
  RecurrenceEnd,
  RecurrenceFrequency,
} from "../../../../enums";
import { JobModel } from "../../../../models";
import { RecurrenceRule } from "../../../../types";
import { theme } from "../../../../utils";

type CustomRecurrenceDialogProps = {
  open: boolean;
  date: Moment;
  rule: RecurrenceRule;
  onClose: () => void;
  onDone: (rule: RecurrenceRule) => void;
};

const isWholeNumberInRange = (value: number, max: number) =>
  Number.isInteger(value) && value >= 1 && value <= max;

export const CustomRecurrenceDialog = ({
  open,
  date,
  rule,
  onClose,
  onDone,
}: CustomRecurrenceDialogProps) => {
  const [draft, setDraft] = useState<RecurrenceRule>(rule);
  const isValid =
    isWholeNumberInRange(draft.interval, RECURRENCE_MAX_INTERVAL) &&
    (draft.end !== RecurrenceEnd.After ||
      isWholeNumberInRange(draft.occurrences, RECURRENCE_MAX_OCCURRENCES)) &&
    (draft.end !== RecurrenceEnd.On ||
      (draft.endDate?.isValid() && !draft.endDate.isBefore(date, "day")));

  const updateDraft = (changes: Partial<RecurrenceRule>) =>
    setDraft((prev) => ({ ...prev, ...changes }));

  const toggleWeekDay = (day: number) => {
    const isSelected = draft.weekDays.includes(day);
    if (isSelected && draft.weekDays.length === 1) return;

    updateDraft({
      weekDays: isSelected
        ? draft.weekDays.filter((item) => item !== day)
        : [...draft.weekDays, day],
    });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Custom recurrence"
      content={
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, my: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Typography sx={{ flexShrink: 0 }}>Repeat every</Typography>
            <Box sx={{ width: 80 }}>
              <TextField
                type="number"
                label=""
                name="interval"
                allowScientificNotation={false}
                value={Number.isNaN(draft.interval) ? "" : draft.interval}
                onChange={(e) =>
                  updateDraft({ interval: parseInt(e.target.value, 10) })
                }
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <SelectBox
                label=""
                name="frequency"
                dropdown={RECURRENCE_FREQUENCY_DROPDOWN.map((item) => ({
                  ...item,
                  key: draft.interval > 1 ? `${item.key}s` : item.key,
                }))}
                value={draft.frequency}
                onChange={(e) =>
                  updateDraft({
                    frequency: e.target.value as RecurrenceFrequency,
                  })
                }
              />
            </Box>
          </Box>

          {draft.frequency === RecurrenceFrequency.Week && (
            <Box>
              <Typography sx={{ mb: 1 }}>Repeat on</Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                {RECURRENCE_WEEKDAY_INITIALS.map((initial, day) => {
                  const isSelected = draft.weekDays.includes(day);

                  return (
                    <Box
                      key={day}
                      role="checkbox"
                      aria-checked={isSelected}
                      aria-label={moment().day(day).format("dddd")}
                      tabIndex={0}
                      onClick={() => toggleWeekDay(day)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggleWeekDay(day);
                        }
                      }}
                      sx={{
                        width: 32,
                        height: 32,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "50%",
                        fontSize: 12,
                        fontWeight: 600,
                        cursor: "pointer",
                        backgroundColor: isSelected
                          ? theme.palette.primary.main
                          : "var(--dashboard-track-segmented)",
                        color: isSelected
                          ? theme.palette.common.white
                          : "var(--dashboard-text-primary)",
                      }}
                    >
                      {initial}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {draft.frequency === RecurrenceFrequency.Month && (
            <SelectBox
              label=""
              name="monthlyRepeatBy"
              dropdown={JobModel.buildMonthlyRepeatDropdown(date)}
              value={draft.monthlyRepeatBy}
              onChange={(e) =>
                updateDraft({
                  monthlyRepeatBy: e.target.value as MonthlyRepeatBy,
                })
              }
            />
          )}

          <Box>
            <Typography sx={{ mb: 1 }}>Ends</Typography>
            <RadioGroup
              value={draft.end}
              onChange={(e) =>
                updateDraft({ end: e.target.value as RecurrenceEnd })
              }
              sx={{ gap: 1.5 }}
            >
              <FormControlLabel
                value={RecurrenceEnd.Never}
                control={<Radio size="small" />}
                label="Never"
              />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FormControlLabel
                  value={RecurrenceEnd.On}
                  control={<Radio size="small" />}
                  label="On"
                  sx={{ width: 90, flexShrink: 0 }}
                />
                <DatePicker
                  label=""
                  name="endDate"
                  defaultValue={draft.endDate}
                  minDate={date}
                  disabled={draft.end !== RecurrenceEnd.On}
                  onChange={(value: Moment) => updateDraft({ endDate: value })}
                />
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FormControlLabel
                  value={RecurrenceEnd.After}
                  control={<Radio size="small" />}
                  label="After"
                  sx={{ width: 90, flexShrink: 0 }}
                />
                <TextField
                  type="number"
                  label=""
                  name="occurrences"
                  allowScientificNotation={false}
                  disabled={draft.end !== RecurrenceEnd.After}
                  value={
                    Number.isNaN(draft.occurrences) ? "" : draft.occurrences
                  }
                  onChange={(e) =>
                    updateDraft({ occurrences: parseInt(e.target.value, 10) })
                  }
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          occurrences
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Box>
            </RadioGroup>
          </Box>
        </Box>
      }
      primaryButtonText="Done"
      secondaryButtonText="Cancel"
      onPrimaryButtonClick={() => onDone(draft)}
      onSecondaryButtonClick={onClose}
      disablePrimaryButton={!isValid}
      sx={{
        modal: { width: { xs: "90%", sm: 420 } },
      }}
    />
  );
};
